import { useCallback } from 'react';
import classNames from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Container from '@ui/container/container';
import convertSecondsToMinutes from '@/utils/convert-seconds-to-minutes';
import { Shot } from '@/types/greenroom/shots.types';
import { MediaData, MediaItem } from '@/types/greenroom/media.types';
import Text from '@ui/text/text/text';
import { GreenroomPlaceholderShot } from './placeholder-shots';
import HoverCard from '@ui/cards/hover-card/hover-card';
import HoverContent from '@ui/cards/hover-card/hover-content';
import GradientButton from '@ui/buttons/gradient-button/gradient-button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastContainerIds, EToastTypes } from '@ui/toast/toast.types';

import styles from './shot-card.module.css';
import OptionalComponent from '@ui/optional-component/optional-component';

type ShotCardProps = {
  mediaItem?: MediaItem & { duration?: number };
  shot?: Shot;
  placeholderShot?: GreenroomPlaceholderShot;
  id: string;
  disableHover?: boolean;
  disableEditing?: boolean;
  fitContainer?: boolean;
};

const ShotCard = ({
  id,
  mediaItem,
  shot,
  placeholderShot,
  disableHover,
  disableEditing,
  fitContainer,
}: ShotCardProps) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({ id });

  // page indexed at 1
  const getCurrentPageThumb = useCallback(
    (data: MediaData = { thumbnail_map: {}, image_array: [''] }, page: number = 1): string => {
      const { thumbnail_map: thumbs, image_array: images } = data;
      const currentPage = page ?? 1;
      const image = images[currentPage - 1] ?? '';
      return thumbs[image] ?? image ?? '';
    },
    []
  );

  const getThumbImage = useCallback(
    (mediaItem: MediaItem) => {
      return getCurrentPageThumb(mediaItem?.media_data);
    },
    [getCurrentPageThumb]
  );

  const backgroundImage = placeholderShot ? placeholderShot.thumbnail_url : mediaItem && getThumbImage(mediaItem);
  const width = fitContainer ? 'initial' : '241px';

  return (
    <Container
      id={id}
      ref={setNodeRef}
      minWidth={width}
      maxWidth={width}
      style={{
        transform: CSS.Transform.toString(transform),
        scrollSnapAlign: 'start',
      }}
    >
      <HoverCard
        backgroundImg={backgroundImage}
        height="135px"
        containerClassName="bg-image-cover radius-8 margin-b-44 overflow-visible"
        disableHover={disableHover}
      >
        <OptionalComponent display={!disableEditing}>
          <HoverContent backgroundColor="rgba(0, 0, 0, 0.5)">
            <Container className="flex fd-row jc-center ai-center gap-8" width="100%" height="100%">
              <GradientButton
                fullBackgroundGradient
                adminButton
                height="32px"
                width="83px"
                padding="0"
                hoverOpacity={0.8}
                wrapperClassNames="no-swipe"
                buttonProps={{
                  onClick: () => {
                    toastTrigger('WIP', {
                      type: EToastTypes.warning,
                      closeOnClick: true,
                      containerId: EToastContainerIds.stacked,
                    });
                  },
                }}
              >
                <Text className="L2">Shot Info</Text>
              </GradientButton>
              <Button
                className="square-32 radius-full padding-0 flex-column jc-center ai-center no-swipe"
                backgroundColor="var(--gray-600)"
                hoverBackgroundColor="var(--gray-500)"
                onClick={() => {
                  toastTrigger('WIP', {
                    type: EToastTypes.warning,
                    closeOnClick: true,
                    containerId: EToastContainerIds.stacked,
                  });
                }}
              >
                <Icon name={ICONS.EDIT} size={12} />
              </Button>
            </Container>
          </HoverContent>
        </OptionalComponent>
        <div className="flex fd-row jc-flex-start ai-center margin-t-8 position-absolute top-100-percent width-100-percent">
          <Button
            height="36px"
            className={classNames(
              'clear-button',
              'flex',
              'fd-row',
              'ai-center',
              'width-24',
              'overflow-hidden',
              styles['shot-position'],
              {
                'no-swipe': !!shot,
              }
            )}
            /* setting pointerEvents to none when button disabled allows the carousel to be clicked and dragged over this element */
            style={{ cursor: 'grabbing', pointerEvents: disableEditing || !shot ? 'none' : 'auto' }}
            hoverBackgroundColor="var(--gray-700)"
            disabled={!shot || disableEditing}
            {...(shot ? attributes : {})}
            {...(shot ? listeners : {})}
          >
            <OptionalComponent display={!disableEditing}>
              <Icon name={ICONS.THREE_DOTS_HORIZONTAL} color="var(--gray-500)" />
            </OptionalComponent>
            <Text tag="span" className="S3 width-24 flex fd-row jc-center ai-center" color="var(--gray-500)">
              {placeholderShot ? placeholderShot.order : (shot?.shot_data.order || 0) + 1}
            </Text>
          </Button>

          <div>
            <Text className="S1" clamp={1} whiteSpace="normal">
              {/* dangerouslySetInnerHTML so that unicode characters properly render */}
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    placeholderShot?.name ||
                    shot?.shot_data.shotTitle ||
                    mediaItem?.custom_name ||
                    mediaItem?.download_name ||
                    mediaItem?.original_name ||
                    '',
                }}
              />
            </Text>

            <div>
              <Text className="P2" color="var(--gray-500)" clamp={1} whiteSpace="normal">
                {placeholderShot ? placeholderShot.duration : convertSecondsToMinutes(Number(mediaItem?.duration || 0))}
              </Text>
            </div>
          </div>
        </div>
      </HoverCard>
    </Container>
  );
};

export default ShotCard;
